import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Img, { ImgProps } from "@insite/mobius/Img";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

interface ImagePanelModel {
    fields: {
        topText: string;
        bottomText: string;
        imageUrl: string;
        altText: string;
        imageLink: LinkFieldValue;
    };
}

const enum fields {
    topText = "topText",
    bottomText = "bottomText",
    imageUrl = "imageUrl",
    altText = "altText",
    imageLink = "imageLink",
    imagePanels = "imagePanels",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.topText]: string;
        [fields.bottomText]: string;
        [fields.imageUrl]: string;
        [fields.altText]: string;
        [fields.imageLink]: LinkFieldValue;
        [fields.imagePanels]: ImagePanelModel[];
    };
    extendedStyles?: ImagePanelListStyles;
}

export interface ImagePanelListStyles {
    wrapper?: InjectableCss;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
    imageLink?: LinkPresentationProps;
    outerContainer?: GridContainerProps;
    outerGridItem?: GridItemProps;
    container?: GridContainerProps;
    topTextGridItem?: GridItemProps;
    topText?: TypographyProps;
    imageGridItem?: GridItemProps;
    bottomTextGridItem?: GridItemProps;
    bottomText?: TypographyProps;
}

export const imagePanelListStyles: ImagePanelListStyles = {
    outerContainer: {
        gap: 0,
        css: css`
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding-top: 10px;
                        padding-bottom: 10px;
                    `,
                    css`
                        padding-top: 10px;
                        padding-bottom: 10px;
                    `,
                    css`
                        padding-top: 25px;
                        padding-bottom: 25px;
                    `,
                    css`
                        padding-top: 25px;
                        padding-bottom: 25px;
                    `,
                    css`
                        padding-top: 25px;
                        padding-bottom: 25px;
                    `,
                ]);
            }}
        `,
    },
    outerGridItem: {
        width: [6, 6, 3, 3, 3],
    },
    container: {
        css: css`
            padding-bottom: 15px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        box-shadow: rgb(0 0 0 / 20%) 0px 3px 15px;
                        margin: 5px 5px;
                    `,
                    css`
                        box-shadow: rgb(0 0 0 / 20%) 0px 3px 15px;
                        margin: 5px 5px;
                    `,
                    null,
                    null,
                    null,
                ]);
            }}
        `,
        gap: 0,
    },
    topTextGridItem: {
        css: css`
            text-align: center;
            vertical-align: bottom;
            padding-bottom: 12px;
            align-items: flex-end;
            height: 60px;
        `,
        width: 12,
    },
    topText: {
        css: css`
            color: rgb(15, 15, 15);
            font-weight: 700;
            font-size: 16px;
            width: 100%;
            text-decoration: underline;
        `,
    },
    imageGridItem: {
        css: css`
            width: auto;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        height: 73px;
                    `,
                    css`
                        height: 73px;
                    `,
                    css`
                        height: 146px;
                    `,
                    css`
                        height: 146px;
                    `,
                    css`
                        height: 146px;
                    `,
                ]);
            }}
            justify-content: center;
        `,
        width: 12,
    },
    imageLink: {
        css: css`
            > span {
                width: auto;
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            height: 73px;
                        `,
                        css`
                            height: 73px;
                        `,
                        css`
                            height: 146px;
                        `,
                        css`
                            height: 146px;
                        `,
                        css`
                            height: 146px;
                        `,
                    ]);
                }}
                justify-content: center;
            }
        `,
    },
    img: {
        css: css`
            max-height: 100%;
            width: auto;
        `,
    },
    bottomTextGridItem: {
        css: css`
            align-items: flex-start;
            text-align: center;
            padding-top: 12px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        height: 60px;
                    `,
                    css`
                        height: 60px;
                    `,
                    css`
                        height: 60px;
                    `,
                    css`
                        height: 72px;
                    `,
                    css`
                        height: 72px;
                    `,
                ]);
            }}
        `,
        width: 12,
    },
    bottomText: {
        css: css`
            color: rgb(15, 15, 15);
            font-weight: 700;
            font-size: 16px;
            width: 100%;
            padding: 0 6px;
        `,
    },
};

export const ImagePanelList: FC<OwnProps> = ({ fields }) => {
    const styles = useMergeStyles("image", imagePanelListStyles);
    return (
        <GridContainer {...styles.outerContainer} data-test-selector="imagePanelList_outerContainer">
            {fields.imagePanels.map((imagePanel, index) => {
                const { url } = useGetLink(imagePanel.fields.imageLink);
                const image = (
                    <Img
                        src={imagePanel.fields.imageUrl}
                        altText={imagePanel.fields.altText}
                        loading="lazy"
                        {...styles.img}
                    />
                );
                return (
                    <GridItem
                        {...styles.outerGridItem}
                        key={`imagePanel${imagePanel.fields.imageLink.value}`}
                        data-test-selector="imagePanel_outerGridItem"
                    >
                        <GridContainer {...styles.container} data-test-selector="imagePanel_container">
                            <GridItem {...styles.topTextGridItem} data-test-selector="imagePanel_topTextGridItem">
                                <Typography {...styles.topText}>
                                    {parse(imagePanel.fields.topText, parserOptions)}
                                </Typography>
                            </GridItem>
                            <GridItem {...styles.imageGridItem} data-test-selector="imagePanel_imageGridItem">
                                {url ? (
                                    <Link href={url} {...styles.imageLink}>
                                        {image}
                                    </Link>
                                ) : (
                                    image
                                )}
                            </GridItem>
                            <GridItem {...styles.bottomTextGridItem} data-test-selector="imagePanel_bottomTextGridItem">
                                <Typography {...styles.bottomText}>
                                    {parse(imagePanel.fields.bottomText, parserOptions)}
                                </Typography>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                );
            })}
        </GridContainer>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: ImagePanelList,
    definition: {
        group: "Basic",
        icon: "image",
        fieldDefinitions: [
            {
                name: fields.imagePanels,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: ({ fields: { topText } }) => topText || "Image Panel",
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: fields.topText,
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: fields.bottomText,
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: fields.imageUrl,
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: fields.altText,
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: fields.imageLink,
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
